import { useState, useEffect, useRef } from "react";

/**
 * Date-scoped localStorage hook for daily game data.
 * Stores/fetches data under a key like `kisekidle-{mode}-YYYY-M-D`.
 * Falls back to the legacy non-date-keyed format for migration compatibility.
 * Automatically re-loads when targetDateStr changes (handles archive date switching).
 *
 * @param {string} baseKey - Base localStorage key (e.g. "kisekidle-chars").
 * @param {string} targetDateStr - Date string in "YYYY-M-D" format.
 * @returns {[Array, Function]} Tuple of [guessesArray, setGuessesArray].
 */
export function useDailyLocalStorage(baseKey, targetDateStr) {
  const storageKey = `${baseKey}-${targetDateStr}`;

  // Helper function to handle the loading logic
  const loadData = (key, dateStr) => {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      return JSON.parse(storedData).guesses || []; 
    }
    // MIGRATION BACKUP
    const oldLegacyData = localStorage.getItem(baseKey);
    if (oldLegacyData) {
      const parsedOld = JSON.parse(oldLegacyData);
      if (parsedOld.date === dateStr) return parsedOld.guesses || [];
    }
    return [];
  };

  const [value, setValue] = useState(() => loadData(storageKey, targetDateStr));
  const [prevKey, setPrevKey] = useState(storageKey); 

  // Fixes the Archive Bug: instantly loads new date data if the date string changes
  if (storageKey !== prevKey) {
    setPrevKey(storageKey);
    setValue(loadData(storageKey, targetDateStr));
  }

  useEffect(() => {
    // Grab any existing data (like the hasWon flag!) before saving
    const existingData = JSON.parse(localStorage.getItem(storageKey) || "{}");
    
    // Merge the existing data with the updated guesses so nothing gets deleted
    const dataToSave = { 
      ...existingData, 
      date: targetDateStr, 
      guesses: value 
    };
    
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
  }, [value, storageKey, targetDateStr]);

  return [value, setValue];
}

/**
 * Generic persistent localStorage hook for settings/preferences.
 * Writes to localStorage on every value change.
 *
 * @param {string} storageKey - The localStorage key to use.
 * @param {*} initialValue - Default value when nothing is stored yet.
 * @returns {[*, Function]} Tuple of [value, setValue].
 */
export function usePersistentStorage(storageKey, initialValue) {
  const [value, setValue] = useState(() => {
    const storedData = localStorage.getItem(storageKey);
    return storedData ? JSON.parse(storedData) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(value));
  }, [value, storageKey]);

  return [value, setValue];
}

/**
 * Keyboard navigation hook for autocomplete suggestion lists.
 * Handles ArrowUp/Down, Enter/Tab to select, and Escape to dismiss.
 * Scrolls the focused item into view automatically.
 *
 * @param {Array} suggestions - Current list of suggestion objects.
 * @param {(item: Object) => void} onSelect - Callback invoked with the selected suggestion item.
 * @returns {{ focusedIndex: number, setFocusedIndex: Function, handleKeyDown: Function, listRef: React.RefObject }}
 */
export function useKeyboardNavigation(suggestions, onSelect) {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef(null);

  const handleKeyDown = (e) => {
    if (!suggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
        onSelect(suggestions[focusedIndex]);
      } else {
        onSelect(suggestions[0]);
      }
      setFocusedIndex(-1);
    } else if (e.key === "Escape") {
      setFocusedIndex(-1);
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex];

      if (focusedElement) {
        focusedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);

  return { focusedIndex, setFocusedIndex, handleKeyDown, listRef };
}
